import { cacheSet, cloneObject, error, getAssetKey, warn } from "./utils";


export default class MlsdkWrapper {
    constructor() {
        this.mlsdkBucket = 'https://d3j8dorfumtm6h.cloudfront.net/packages';
        this.mlsdkUrl = `${this.mlsdkBucket}/apc-mlsdk/0.43.0/js`;
        this.mlsdk = null;
    }

    /**
     * Validate that the item has all necessary params
     * @param item
     * @returns {boolean}
     */
    validateItem(item) {
        if (!item.skuCode) {
            error("Validation Error", "skuCode is empty");
        }
        if (!item.productCode) {
            error("Validation Error", "productCode is empty");
        }
        if (!item.photoIDs || item.photoIDs.length === 0) {
            error("Validation Error", "photoIDs is empty");
        }
        return true;
    }


    /**
     * Init mlsdk package, setThe workers and initialize mlsdk
     * @param userId
     * @param env
     */
    initMlsdk(userId, env) {
        this.mlsdk.setWorkerPaths(this.mlsdkUrl);
        const initArgs = {
            partner: 'sfly',
            appVersion: '1.1.1',
            appName: 'magic_renderer',
            userId: userId,
            env: env,
            client: window.innerWidth < 720 ? 'mobileweb' : 'web',
        };

        this.mlsdk.initialize(initArgs);
    }

    /**
     * Handle getSurface response, set in cache the results by certain params
     * @param resultItems
     * @param items
     */
    handleGetSurfacesResult(resultItems, items) {
        const returnedKeys = Object.keys(resultItems);

        if (returnedKeys.length > 0) {
            for (const key of returnedKeys) {

                items.forEach(item => {
                    let splittedKey = key.split("-");
                    splittedKey = splittedKey.map(fragment => fragment.split("="));
                    splittedKey = Object.fromEntries(splittedKey)
                    const { brand, skuCode, productCode } = !splittedKey.brand ? {
                        ...splittedKey,
                        brand: "SFLY"
                    } : splittedKey;

                    if (item.brand === brand && parseInt(item.productCode) === parseInt(productCode) && parseInt(item.skuCode) === parseInt(skuCode)) {
                        const schemeData = JSON.parse(resultItems[key]?.result?.responseData)
                        item.layoutData = cloneObject(schemeData);
                        const assetKey = getAssetKey(item, item.isMlsdk);
                        cacheSet(assetKey, schemeData);
                    }
                });

            }

        } else {
            error("MagicRenderer:: getSurfaceData", `missing schemes for items`);
        }
    }

    /**
     * First indexPhotos, then convert scheme to liteSurface, Finally, trigger mlsdk.photos.designSurface
     */
    async designSurface(item, createParamsForSurfaceWrapper, MagicRenderer) {
        try {
            let indexedPhotosSet = new Set();
            if (item.photoIDs) {
                // Indexing the photos using mlsdk
                const indexedPhotos = await this.mlsdkIndexPhotos(item.photoIDs)

                if (indexedPhotos) {
                    await indexedPhotos.subscribe({
                        next: photo => {
                            // Called every indexed photo
                            indexedPhotosSet.add(photo.photoId)
                        },
                        error: e => {
                            warn("Error indexing photo", e)
                        },
                        complete: async () => {
                            try {
                                // Called when all photos indexed
                                const liteSurface = this.convertToLiteSurface(item)

                                const params = {
                                    surface: liteSurface,
                                    photoIds: Array.from(indexedPhotosSet)
                                }

                                await createParamsForSurfaceWrapper(item, this.mlsdk.photos.designSurface(params), MagicRenderer);
                            } catch (e) {
                                item.onError(e);
                            }
                        }
                    })
                } else {
                    error("Error design surface - failed to index photos");
                }
            }

        } catch (e) {
            error("Error design surface", e.message);
        }
    };

    /**
     * Inner method that being called from designSurface, will trigger mlsdk.indexPhotos
     * return an observable object
     * @private
     */
    async mlsdkIndexPhotos(photoIDs) {
        try {
            return await this.mlsdk.indexPhotos(photoIDs)
        } catch (e) {
            error("MagicRenderer:: mlsdkIndexPhotos",
                `failed to index mlsdk photos`);
        }
    }

    /**
     * Inner method that being called from designSurface, will trigger mlsdk.converter.convertBundleCreationScheme
     * return liteSurface
     */
    convertToLiteSurface(item) {
        try {
            const productScheme = this.extractProductScheme(item.layoutData, item.formFactor)

            // Extract the correct layout using layout name
            productScheme.style.assetLists = productScheme?.style?.assetLists.filter(assetList => assetList.assetType.toLowerCase() === "layout");
            const assetList = productScheme.style.assetLists.find(assetList => assetList?.assetType.toLowerCase() === "layout");
            const layout = assetList.items.find(i => i.id === item.layoutName);
            assetList.items = [layout]

            const liteScheme = this.mlsdk.converter.convertBundleCreationScheme(item.layoutData);

            item.styleId = liteScheme[0].styleId;

            return liteScheme[0]?.surfaces[item.layoutName]?.surface;
        } catch (e) {
            error("MagicRenderer:: convertToLiteSurface",
                `failed convert scheme to liteSurface in product sku ${item.skuCode}`);
        }
    }

    /**
     * Extract productScheme from bundle creation scheme by form factor
     * @param bundleCreationScheme
     * @param formFactor
     * @returns {*}
     */
    extractProductScheme(bundleCreationScheme, formFactor) {
        const key = bundleCreationScheme?.productHierarchy.find(productH => productH.key === formFactor)?.key;
        const productSchemeKey = key && bundleCreationScheme.productDictionary[key]?.productScheme;
        return productSchemeKey && bundleCreationScheme.productSchemes[productSchemeKey];
    }

    /**
     * Extract default layout from scheme
     * @param bundleCreationScheme
     * @param formFactor
     * @returns {*}
     */
    extractDefaultLayoutName(bundleCreationScheme, formFactor) {
        return bundleCreationScheme?.productDictionary[formFactor]?.defaultLayouts[0]?.name;
    }


    /**
     * Replace the sflyams orig url to small gim asset url
     * @param url
     * @param env
     * @returns {string}
     */
    replaceAssetUrl(url, env) {
        const gimUrl = url.replace("sflyams://", this.getAssetFetchUrl(url, env))
        return gimUrl.replace("orig", "200");
    }

    /**
     * Get asset fetch Url by environment
     * @private
     */
    getAssetFetchUrl(assetUrl, env) {
        switch (env) {
            case 'dev':
            case 'beta':
            case 'kappa':
                return `https://gim.${env}.shutterfly.com/asset/fetch`;
            case 'stage':
                return 'https://gim1.stage.shutterfly.com/asset/fetch';
            default:
                return 'https://im1.shutterfly.com/asset/fetch';
        }
    }

    /**
     * Converts item and designSurface response into SurfaceWrapper input
     * @param item
     * @param surfaceWithPhotos
     * @param extra
     * @returns {any}
     */
    getSurfaceWrapperParams(item, surfaceWithPhotos, extra) {
        const imageAssets = surfaceWithPhotos?.layout?.assets.filter(asset => asset.type === "Image");
        const textAssets = surfaceWithPhotos?.layout?.assets.filter(asset => asset.type === "Text");
        const embellishmentAssets = surfaceWithPhotos?.layout?.assets.filter(asset => asset.type === "Embellishment");
        const backgroundAsset = surfaceWithPhotos?.layout?.assets.find(asset => asset.type === "Background");
        const photos = imageAssets.map(imageAsset => {
            return {
                id: imageAsset.imageId,
                url: item.photoIDs.find(photo => photo.photoId === imageAsset.imageId)?.url
            }
        })
        const photoAssignment = imageAssets.map(imageAsset => {
            return {
                photoId: imageAsset.imageId,
                url: item.photoIDs.find(photo => photo.photoId === imageAsset.imageId)?.url,
                imageAreaId: imageAsset.imageId && imageAsset.id,
                crop: imageAsset.crop,
            }
        })

        const imageAreas = imageAssets.map(
            imageAsset => {
                return {
                    id: imageAsset.id,
                    x: imageAsset.container.x,
                    y: imageAsset.container.y,
                    width: imageAsset.container.width,
                    height: imageAsset.container.height,
                    orientation: imageAsset.container.width > imageAsset.container.height ? "landscape" : "portrait",
                    areaType: "image.dynamic",
                    url: photos.find(photo => photo.id === imageAsset.imageId)?.url,
                    crop: imageAsset.crop,
                    canFail: true,
                    colorGroup: {},
                    seqno: 255,
                }
            }
        )

        const embellishmentAreas = embellishmentAssets.map(
            embellishmentAsset => {
                return {
                    id: embellishmentAsset.id,
                    x: embellishmentAsset.container.x,
                    y: embellishmentAsset.container.y,
                    width: embellishmentAsset.container.width,
                    height: embellishmentAsset.container.height,
                    url: this.replaceAssetUrl(embellishmentAsset.sourceUrl, extra.env),
                    canFail: true,
                    colorGroup: {},
                    seqno: 255,
                    crop: embellishmentAsset.container.crop || [0, 0, 1, 1]
                }
            }
        )

        const textAreas = textAssets.map(
            textAsset => {
                return {
                    id: textAsset.id,
                    x: textAsset.container.x,
                    y: textAsset.container.y,
                    width: textAsset.container.width,
                    height: textAsset.container.height,
                    areaType: "text.body",
                    subtype: "body",
                    t: true,
                    colorGroup: {},
                    seqno: 255,
                    text: textAsset.text ? textAsset.text : "",
                    defaultFont: {
                        color: "#000000", // missed, have colorId
                        id: textAsset.fontId,
                        name: textAsset.fontName,
                        pointSize: textAsset.pointSize,
                        horizontalJustification: "center", // missed
                        verticalJustification: "middle" // missed
                    }

                }
            }
        )

        const hasTextData = textAreas.length > 0;
        const newLayoutData = {
            thumbUrl: item.layoutData.thumbUrl,
            name: item.layoutName,
            printableAreaX: 0,
            printableAreaY: 0,
            printableAreaWidth: surfaceWithPhotos.layout.width / 72,
            printableAreaHeight: surfaceWithPhotos.layout.height / 72,
            imageAreas,
            textAreas,
            embellishmentAreas,
            backgroundUrl: backgroundAsset?.sourceUrl || item.layoutData?.defaultBackgroundUrl || "",
        }

        return Object.assign({
            hasTextData,
            sizeId: item.sizeId,
            styleId: item.styleId,
            layoutName: item.layoutName,
            photos,
            photoAssignment,
            layoutData: newLayoutData,
            isMlsdk: true,
        }, extra)
    }

    /**
     * Create projectJson for mlsdk flow from the items
     * @param item
     * @param params
     * @returns {{product: {photoStrip: *, surfaces: [{canvas: {layout: {textAreas: *, layoutId: *, photos: *}}, type: string}], info: {sizeId: *, productCode: *, styleId: *, occasionId: *, sku: *, userId: *, productType: (string|string|*)}}}}
     */
    createProjectJson(item, params) {
        const info = {
            occasionId: item.occasionId,
            styleId: item.styleId,
            sizeId: item.sizeId,
            userId: item.userId,
            productType: item.productType,
            sku: item.skuCode,
            productCode: item.productCode,
            addons: item.addons,
        }

        const photoStrip = params.photos.filter(photo => photo.url).map((photo, index) => {
            return {
                photoId: photo.id,
                url: photo.url,
                areaOfInterest: [0, 0, 1, 1],
                photoRefId: index + 1,
            }
        })

        let photos = [];
        params.photoAssignment.forEach((photoObj, index) => {
            if (photoObj.url) {
                const photoRefId = photoStrip.find(photo => photo.photoId === photoObj.photoId)?.photoRefId
                photos.push({
                    finalCrop: photoObj.crop,
                    seqNum: index,
                    photoRefId,
                })
            }
        })

        const surfaces = [
            {
                type: "front",
                canvas: {
                    layout: {
                        layoutId: item.layoutName,
                        photos,
                        textAreas: params.layoutData.textAreas.map(text => {
                            return {
                                id: text.id,
                                font: text.defaultFont,
                                text: text.text,
                                ignore: true,
                            }
                        }
                        ),
                    }
                }
            }
        ]


        return {
            product: {
                info,
                surfaces,
                photoStrip,
            }
        }
    }

    /**
     * Create liteProduct for mlsdk flow, liteProduct wrapping for Naut creation path
     * @param item
     * @param layoutWithPhotos
     */
    createLiteProduct(item, layoutWithPhotos) {
        const imageAssets = layoutWithPhotos?.layout?.assets.filter(asset => asset.type === "Image") || [];
        const selectedImages = imageAssets.map(imageAsset => imageAsset.imageId);
        const productImages = item.photoIDs.filter(photo => selectedImages.includes(photo.photoId)).map(photo => {
            return {
                origin: photo.source,
                imageId: photo.photoId,
                width: photo.metadata.width,
                height: photo.metadata.height,
                locationSpec: photo.locationSpec,
            }
        }
        )

        // Change idea pages to pages, idea page are only on photobook
        if (item.productType !== "photobook" && layoutWithPhotos.surfaceType === "ideapage") {
            layoutWithPhotos.surfaceType = "page"
        }

        productImages.forEach((image, index) => {
            const asset = imageAssets.find(asset => asset.imageId === image.imageId)
            if (asset) {
                asset.height = image.height;
                asset.width = image.width;
                asset.localId = index;
            }
        })

        return {
            title: "",
            guid: "",
            applicationOrigin: "WEB",
            images: productImages,
            products: [{
                formFactorId: item.formFactor,
                skuCode: item.skuCode,
                productCode: parseInt(item.productCode),
                productGuid: "",
                productType: item.productType,
                selections: item.addons,
                surfaces: [layoutWithPhotos]
            }],
        }
    }
}





